/**
 * Payroll — LO commission automation (docs/Payroll/PLAN.md).
 *
 * Route-only page (/payroll): deliberately NOT in the sidebar, drawer, nav
 * registries, or tours until Mitchell approves the finished feature.
 *
 * Access: PayrollAdmin || SuperAdmin, enforced server-side by every callable;
 * this page renders an access-denied state on the first permission-denied.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';
import { ENGINE_VERSION } from './engineClient';
import { payrollApi, isPermissionDenied } from './api';
import { useAuth } from '../../contexts/AuthContext';
import RunsTab from './Runs/RunsTab';
import CalculatorTab from './Calculator/CalculatorTab';
import PipelineTab from './Pipeline/PipelineTab';
import ScheduleTab from './Schedule/ScheduleTab';
import ReferenceTab from './Reference/ReferenceTab';
import ImportWizard from './Reference/ImportWizard';
import ConfigPanel from './Config/ConfigPanel';
import PayrollAuditViewer from './Audit/PayrollAuditViewer';

export default function PayrollPage() {
  const subtleText = useColorModeValue('gray.600', 'gray.400');
  const toast = useToast();
  const { SuperAdmin } = useAuth();
  const isSuperAdmin = !!SuperAdmin;

  const [runs, setRuns] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [calcLoadRow, setCalcLoadRow] = useState(null); // backtest → calculator (§9.5)
  const [calcPresetEmail, setCalcPresetEmail] = useState(null); // reference → calculator
  const [runsOpenRunId, setRunsOpenRunId] = useState(null); // schedule → runs detail
  // Bumping this re-fetches everything: the calculator reloads snapshot +
  // pipeline counts in place (inputs preserved); other tabs remount via key.
  const [refreshNonce, setRefreshNonce] = useState(0);

  const refreshRuns = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { runs: rows } = await payrollApi.listRuns({ limit: 50 });
      setRuns(rows);
    } catch (err) {
      if (isPermissionDenied(err)) setDenied(true);
      else setLoadError(err.message || 'Failed to load runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshRuns(); }, [refreshRuns]);

  const refreshAll = () => {
    setRefreshNonce((n) => n + 1);
    refreshRuns();
    toast({
      title: 'Refreshing payroll data',
      description: 'Re-reading Google Sheets and reference data…',
      status: 'info',
      duration: 3000,
    });
  };

  if (denied) {
    return (
      <Container maxW="container.md" py={16}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Payroll admin access required</AlertTitle>
            <AlertDescription>
              This area is restricted to Payroll Admins and Super Admins. Ask Mitchell to grant
              the PayrollAdmin role on your user profile.
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <Flex align="baseline" justify="space-between" wrap="wrap" mb={4} gap={2}>
        <Box>
          <Heading size="lg">Payroll</Heading>
          <Text fontSize="sm" color={subtleText}>
            LO commission automation: calculate everything, flag uncertainty.
          </Text>
        </Box>
        <Flex align="center" gap={2}>
          <Button size="sm" variant="outline" leftIcon={<RepeatIcon />} onClick={refreshAll} isLoading={loading && !!runs}>
            Refresh data
          </Button>
          <Badge colorScheme="purple" fontSize="0.75em" px={2} py={1} borderRadius="md">
            {ENGINE_VERSION}
          </Badge>
        </Flex>
      </Flex>

      {loading && !runs ? (
        <Flex justify="center" py={20}><Spinner size="xl" /></Flex>
      ) : (
        <Tabs colorScheme="purple" isLazy index={tabIndex} onChange={setTabIndex}>
          <TabList flexWrap="wrap">
            <Tab>Calculator</Tab>
            <Tab>Pipeline</Tab>
            <Tab>Runs</Tab>
            <Tab>Schedule</Tab>
            <Tab>Reference</Tab>
            <Tab>Import</Tab>
            <Tab>Config</Tab>
            <Tab>Audit</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <CalculatorTab
                loadRowNum={calcLoadRow}
                onRowLoaded={() => setCalcLoadRow(null)}
                presetLoEmail={calcPresetEmail}
                onPresetConsumed={() => setCalcPresetEmail(null)}
                refreshSignal={refreshNonce}
              />
            </TabPanel>
            <TabPanel px={0}>
              <PipelineTab
                isActive={tabIndex === 1}
                refreshSignal={refreshNonce}
                onOpenInCalculator={(rowNum) => { setCalcLoadRow(rowNum); setTabIndex(0); }}
              />
            </TabPanel>
            <TabPanel px={0}>
              <RunsTab
                runs={runs || []}
                loading={loading}
                loadError={loadError}
                onRefresh={refreshRuns}
                onOpenInCalculator={(rowNum) => { setCalcLoadRow(rowNum); setTabIndex(0); }}
                isSuperAdmin={isSuperAdmin}
                openRunId={runsOpenRunId}
                onOpenRunConsumed={() => setRunsOpenRunId(null)}
              />
            </TabPanel>
            <TabPanel px={0}>
              <ScheduleTab
                key={`sch-${refreshNonce}`}
                isSuperAdmin={isSuperAdmin}
                onOpenRun={async (runId) => {
                  setRunsOpenRunId(runId);
                  setTabIndex(2);
                  await refreshRuns();
                }}
              />
            </TabPanel>
            <TabPanel px={0}>
              <ReferenceTab
                key={`ref-${refreshNonce}`}
                isSuperAdmin={isSuperAdmin}
                onTryInCalculator={(email) => { setCalcPresetEmail(email); setTabIndex(0); }}
              />
            </TabPanel>
            <TabPanel px={0}>
              <ImportWizard key={`imp-${refreshNonce}`} isSuperAdmin={isSuperAdmin} payrollApi={payrollApi} />
            </TabPanel>
            <TabPanel px={0}>
              <ConfigPanel key={`cfg-${refreshNonce}`} isSuperAdmin={isSuperAdmin} />
            </TabPanel>
            <TabPanel px={0}>
              <PayrollAuditViewer key={`aud-${refreshNonce}`} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Container>
  );
}
